import { formatMoney, surchargeFor } from '@app/shared'
import { readCivilDate } from './today'

/**
 * Every public payment is taken in two steps: the answers, then the card in
 * their place. This is what the steps ARE, apart from how any page draws them.
 */

/**
 * Whether Back may discard the checkout.
 *
 * Not while a confirmation is in flight, because the bank may still say yes to
 * a checkout the page has already let go of; and not once it has said yes,
 * because there is no longer an answer that could be changed.
 */
export function mayGoBack(state: {
  pending: boolean
  completed: boolean
}): boolean {
  return !state.pending && !state.completed
}

/** What the commit ending the answers says. It charges nothing, so it names no amount. */
export function continueLabel(opening: boolean): string {
  return opening ? 'Opening secure payment…' : 'Continue to payment'
}

/** The line under the total in the strip, where the donor is covering the card cost. */
export function cardCostNote(cardCostCents: number): string | undefined {
  return cardCostCents > 0
    ? `includes ${formatMoney(cardCostCents)} card cost`
    : undefined
}

/**
 * The stored answers a returned payment may be thanked from.
 *
 * A payment that leaves the page and comes back carries only its checkout id,
 * and the answers in storage may belong to a different attempt — a gift tried
 * and left before a pledge was paid. Answers name the payment only when they
 * were stored for that very checkout; anything else is thanked without naming
 * a gift rather than naming the wrong one.
 */
export function answersForCheckout<T extends { checkoutId?: string }>(
  stored: T | null,
  checkoutId: string | null,
): T | null {
  if (!stored || !checkoutId) return null
  return stored.checkoutId === checkoutId ? stored : null
}

export type ThanksRow = { label: string; value: string }

export type Thanks = {
  headline: string
  receipt: string
  record: ThanksRow[]
}

/**
 * The thank-you for a gift.
 *
 * The headline names the GIFT — what the donor chose to give, before any card
 * cost they covered — and never the item: "your gift of $100 toward Sponsor a
 * kiddush" reads as the page reciting its catalog. What it was for belongs in
 * the record under it, beside what was charged.
 *
 * It thanks for what they asked to give rather than claiming the money is
 * banked: the webhook is the record, and it may not have landed yet.
 */
export function giftThanks(gift: {
  amountCents: number
  coverFee: boolean
  /** What it was for. Null for a gift to the shul itself, which names nothing. */
  forLabel: string | null
  /** The question the donor's words answered, and the words. */
  note?: { label: string; words: string } | null
  /** The address the receipt goes to, where one was given. */
  email?: string | null
  /** The civil day it was given, `YYYY-MM-DD`. */
  on: string
  /**
   * Gifts to other campaigns made in the same payment. The thanks is for all
   * of it, and the record says what each part was for.
   */
  beside?: { label: string; amountCents: number }[]
}): Thanks {
  const beside = gift.beside ?? []
  const given =
    gift.amountCents + beside.reduce((sum, b) => sum + b.amountCents, 0)
  const charged = given + (gift.coverFee ? surchargeFor(given) : 0)
  const words = gift.note?.words.trim()
  const parts =
    beside.length > 0
      ? [
          ...(gift.forLabel
            ? [{ label: gift.forLabel, value: formatMoney(gift.amountCents) }]
            : []),
          ...beside.map((b) => ({
            label: b.label,
            value: formatMoney(b.amountCents),
          })),
        ]
      : gift.forLabel
        ? [{ label: 'For', value: gift.forLabel }]
        : []
  return {
    headline: `Thank you for your gift of ${formatMoney(given)}.`,
    receipt: receiptLine(gift.email),
    record: [
      ...parts,
      ...(gift.note && words ? [{ label: gift.note.label, value: words }] : []),
      { label: 'Charged', value: formatMoney(charged) },
      { label: 'Date', value: readCivilDate(gift.on, 'long') },
    ],
  }
}

/**
 * The thank-you for a membership, in its own words: a membership that starts
 * later has charged nothing, and a thank-you for money would be untrue.
 */
export function membershipThanks(membership: {
  tierLabel: string
  startsOn: string
  dueTodayCents: number
  email?: string | null
  on: string
}): Thanks {
  return {
    headline:
      "Your card is saved. We'll email you when your membership is set up.",
    receipt: 'There is nothing else for you to do.',
    record: [
      { label: 'For', value: `${membership.tierLabel} membership` },
      {
        label: 'Starts',
        value: readCivilDate(membership.startsOn, 'long'),
      },
      { label: 'Date', value: readCivilDate(membership.on, 'long') },
    ],
  }
}

/**
 * The thank-you for a monthly or yearly gift: nothing was charged, so it says
 * the card is saved and sends the giver to their mail. It reads the same
 * whether the address opened a new account or must confirm one already on
 * file, so the page never says which addresses are known.
 */
export function standingGiftThanks(gift: {
  amountCents: number
  cadence: 'monthly' | 'annual'
  startsOn: string
  forLabel: string | null
  email: string
}): Thanks {
  const each = gift.cadence === 'annual' ? 'each year' : 'each month'
  return {
    headline: `Your card is saved for a gift of ${formatMoney(gift.amountCents)} ${each}.`,
    receipt: `Check your email at ${gift.email} — we'll confirm your gift there. Each gift is receipted, with a link to stop it.`,
    record: [
      ...(gift.forLabel ? [{ label: 'For', value: gift.forLabel }] : []),
      { label: 'First gift', value: readCivilDate(gift.startsOn, 'long') },
    ],
  }
}

/**
 * A receipt is promised only where there is an address to send it to, and the
 * address is named: a mistyped one is caught here, while the donor is still on
 * the page, rather than by a receipt that never arrives.
 */
function receiptLine(email: string | null | undefined): string {
  const to = email?.trim()
  return to
    ? `Your receipt is on its way to ${to}. There is nothing else for you to do.`
    : 'There is nothing else for you to do.'
}
